/**
 * =============================================================================
 * Componente: ReferralUsageInline
 * Versão: 1.0.0 (2026-05-04)
 *
 * Descrição:
 *   Indicador compacto inline para o módulo Indicação — espelha o padrão visual
 *   do `TasksUsageInline` (Linear/Vercel/Raycast). Exibe 4 chips com mini donut
 *   SVG (14px) representando uso vs limite de Campanhas, Programas, Afiliados
 *   e saldo de Créditos de Conversão.
 *
 *   - Free / Starter / Pro: chips 1-3 com donut proporcional `usage/limit`.
 *   - Unlimited: chips 1-3 mostram apenas a contagem + badge "∞" (sem donut).
 *   - Chip 4 (Créditos): donut sempre baseado em saldo vs (saldo + consumido)
 *     do tier atual; vermelho quando saldo ≤ 0.
 *
 *   Admin recebe atalho `ArrowUpRight` para `/tenant/plans?focus=referral`.
 *
 *   Reusa exatamente o estilo de chip / donut / tooltip do TasksUsageInline
 *   para manter consistência visual no header.
 * =============================================================================
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowUpRight,
  Megaphone,
  Briefcase,
  Users,
  Coins,
  History,
  Infinity as InfinityIcon,
} from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

import { useReferralAccess, type ReferralAccess } from '@/hooks/useReferralAccess';
import { useAuth } from '@/hooks/useAuth';
import { ReferralCreditHistoryModal } from '@/components/referral/billing/ReferralCreditHistoryModal';

/* ────────────────────────────────────────────────────────────────────────── */
/*  Helpers                                                                   */
/* ────────────────────────────────────────────────────────────────────────── */

/**
 * Resolve o tom (cor) do donut/texto baseado no % consumido.
 * Mesmo padrão do TasksUsageInline para preservar consistência visual.
 */
function getTone(pct: number): { stroke: string; text: string } {
  if (pct >= 95) return { stroke: 'stroke-destructive', text: 'text-destructive' };
  if (pct >= 80)
    return { stroke: 'stroke-amber-500', text: 'text-amber-600 dark:text-amber-400' };
  return { stroke: 'stroke-emerald-500', text: 'text-emerald-600 dark:text-emerald-400' };
}

/** Detecta admin para liberar o atalho de upgrade */
function isAdminRole(
  roles: { role: string; tenant_id: string | null }[],
  tenantId?: string,
): boolean {
  if (!tenantId) return false;
  return roles.some(
    (r) =>
      (r.role === 'tenant_admin' && r.tenant_id === tenantId) ||
      (r.role === 'root_admin' && r.tenant_id === null),
  );
}

/** Rótulo amigável do tier atual (free quando não há add-on pago). */
function tierLabel(slug: ReferralAccess['tier_slug']): string {
  switch (slug) {
    case 'referral_starter':
      return 'Starter';
    case 'referral_pro':
      return 'Pro';
    case 'referral_unlimited':
      return 'Unlimited';
    default:
      return 'Free';
  }
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Donut SVG (14px) — arco proporcional ao % usado                           */
/* ────────────────────────────────────────────────────────────────────────── */

interface DonutProps {
  pct: number;
  toneClass: string;
  ariaLabel: string;
}

function Donut({ pct, toneClass, ariaLabel }: DonutProps) {
  const size = 14;
  const stroke = 2.25;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const safePct = Math.min(100, Math.max(0, pct));
  const dashOffset = circumference - (safePct / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel}
      className="shrink-0 -rotate-90"
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        className="stroke-muted-foreground/20"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        strokeWidth={stroke}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={dashOffset}
        className={cn('transition-[stroke-dashoffset] duration-500', toneClass)}
      />
    </svg>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Chip individual                                                           */
/* ────────────────────────────────────────────────────────────────────────── */

interface ChipProps {
  icon: React.ReactNode;
  /** Quando undefined → renderiza badge "∞" (recurso ilimitado, tier Unlimited). */
  pct: number | undefined;
  primaryLabel: string;
  ariaLabel: string;
  tooltipTitle: string;
  tooltipBody: React.ReactNode;
  /** Sobrepõe o tom (ex: chip de créditos vermelho quando saldo = 0). */
  forceTone?: { stroke: string; text: string };
}

function UsageChip({
  icon,
  pct,
  primaryLabel,
  ariaLabel,
  tooltipTitle,
  tooltipBody,
  forceTone,
}: ChipProps) {
  const tone = forceTone ?? (pct !== undefined ? getTone(pct) : getTone(0));

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div
          className={cn(
            'inline-flex items-center gap-1.5 rounded-md border border-border/40',
            'px-2 py-0.5 h-[22px] text-xs tabular-nums',
            'bg-background/60 hover:bg-muted/50 transition-colors cursor-default',
          )}
        >
          {pct === undefined ? (
            <InfinityIcon className="h-3 w-3 text-muted-foreground/70" aria-label={ariaLabel} />
          ) : (
            <Donut pct={pct} toneClass={tone.stroke} ariaLabel={ariaLabel} />
          )}
          <span className="text-muted-foreground/70 hidden sm:inline">{icon}</span>
          <span className={cn('font-medium hidden sm:inline', tone.text)}>{primaryLabel}</span>
        </div>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-[260px] text-xs">
        <div className="font-semibold mb-1">{tooltipTitle}</div>
        {tooltipBody}
      </TooltipContent>
    </Tooltip>
  );
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  Componente principal                                                      */
/* ────────────────────────────────────────────────────────────────────────── */

export interface ReferralUsageInlineProps {
  className?: string;
  /** Esconde a seta de upgrade mesmo para admin */
  hideUpgradeButton?: boolean;
}

export function ReferralUsageInline({
  className,
  hideUpgradeButton = false,
}: ReferralUsageInlineProps) {
  const navigate = useNavigate();
  const { currentTenant, roles } = useAuth();
  const { data: access, isLoading } = useReferralAccess();
  const [historyOpen, setHistoryOpen] = useState(false);

  const isAdmin = isAdminRole(roles, currentTenant?.id);

  // ── Skeleton ─────────────────────────────────────────────────────────
  if (isLoading || !access) {
    return (
      <div className={cn('inline-flex items-center gap-1.5', className)}>
        <Skeleton className="h-[22px] w-16 rounded-md" />
        <Skeleton className="h-[22px] w-16 rounded-md" />
        <Skeleton className="h-[22px] w-16 rounded-md" />
        <Skeleton className="h-[22px] w-16 rounded-md" />
      </div>
    );
  }

  const tier = tierLabel(access.tier_slug);

  // Helper para construir cada chip de recurso (campaigns/programs/affiliates).
  // Quando o limit é null (ilimitado, Unlimited), renderiza chip com badge "∞".
  const buildResourceChip = (
    resource: 'campaigns' | 'programs' | 'affiliates',
    icon: React.ReactNode,
    label: string,
  ) => {
    const limit = access.limits[resource];
    const used = access.usage[resource];
    const isUnlimited = limit === null || limit === undefined;
    const pct = isUnlimited ? undefined : limit > 0 ? (used / limit) * 100 : 100;

    return (
      <UsageChip
        icon={icon}
        pct={pct}
        primaryLabel={isUnlimited ? `${used.toLocaleString('pt-BR')}` : `${used}/${limit}`}
        ariaLabel={`${used} ${label} usados${isUnlimited ? '' : ` de ${limit}`}`}
        tooltipTitle={label}
        tooltipBody={
          <div className="space-y-0.5 text-muted-foreground">
            {isUnlimited ? (
              <div>
                <span className="text-foreground font-medium">Ilimitado</span> no plano{' '}
                <span className="text-foreground">{tier}</span>
              </div>
            ) : (
              <>
                <div>
                  <span className="text-foreground font-medium">{(pct ?? 0).toFixed(1)}%</span>{' '}
                  consumido
                </div>
                <div>
                  Restam{' '}
                  <span className="text-foreground font-medium">
                    {Math.max(0, limit - used).toLocaleString('pt-BR')}
                  </span>
                </div>
                <div>
                  Plano: <span className="text-foreground">{tier}</span>
                </div>
              </>
            )}
          </div>
        }
      />
    );
  };

  // ── Chip de Créditos ────────────────────────────────────────────────
  // Para o donut, usamos saldo vs (saldo + consumido) — apenas no tier free.
  // Tiers pagos têm conversões "ilimitadas" do ponto de vista de bloqueio,
  // mas o chip continua útil mostrando o saldo absoluto.
  const balance = access.credits.balance;
  const isFreeTier = !access.is_paid_tier;
  // % de "esgotamento": 0% = cheio, 100% = vazio. Invertemos para coerir com
  // o getTone (verde quando baixo % consumido).
  const creditsConsumedPct = isFreeTier
    ? balance > 0
      ? 100 - (balance / 20) * 100 // 20 é o baseline cortesia
      : 100
    : balance <= 0
      ? 100
      : 0;
  const creditsTone =
    balance <= 0
      ? { stroke: 'stroke-destructive', text: 'text-destructive' }
      : getTone(creditsConsumedPct);

  return (
    <TooltipProvider delayDuration={150}>
      <div className={cn('inline-flex items-center gap-1.5', className)}>
        {buildResourceChip(
          'campaigns',
          <Megaphone className="h-3 w-3" />,
          'campanhas',
        )}
        {buildResourceChip(
          'programs',
          <Briefcase className="h-3 w-3" />,
          'programas',
        )}
        {buildResourceChip(
          'affiliates',
          <Users className="h-3 w-3" />,
          'afiliados',
        )}

        {/* Chip 4 — Créditos de Conversão
            Admin: Popover clicável (tooltip + botão Histórico)
            Demais: Tooltip simples (comportamento original) */}
        {isAdmin ? (
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                aria-label={`Saldo de ${balance} créditos de conversão. Abrir detalhes`}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-md border border-border/40',
                  'px-2 py-0.5 h-[22px] text-xs tabular-nums',
                  'bg-background/60 hover:bg-muted/50 transition-colors',
                )}
              >
                <Donut
                  pct={isFreeTier ? creditsConsumedPct : balance > 0 ? 0 : 100}
                  toneClass={creditsTone.stroke}
                  ariaLabel="Saldo de créditos"
                />
                <span className="text-muted-foreground/70 hidden sm:inline">
                  <Coins className="h-3 w-3" />
                </span>
                <span className={cn('font-medium hidden sm:inline', creditsTone.text)}>
                  {balance.toLocaleString('pt-BR')}
                </span>
              </button>
            </PopoverTrigger>
            <PopoverContent side="bottom" align="end" className="w-[280px] text-xs p-3">
              <div className="font-semibold mb-1.5 flex items-center gap-1.5">
                <Coins className="h-3.5 w-3.5 text-primary" />
                Créditos de Conversão
              </div>
              <div className="space-y-0.5 text-muted-foreground">
                <div>
                  Saldo:{' '}
                  <span className={cn('font-medium', balance <= 0 ? 'text-destructive' : 'text-foreground')}>
                    {balance.toLocaleString('pt-BR')}
                  </span>
                </div>
                <div>Plano: <span className="text-foreground">{tier}</span></div>
                <div className="text-[11px] opacity-80 pt-1">
                  Cada conversão consome 1 crédito em todos os planos. Estornado automaticamente
                  se o negócio for movido para fase de perda — exceto comissões já pagas.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setHistoryOpen(true)}
                className={cn(
                  'mt-3 w-full inline-flex items-center justify-center gap-1.5 h-7 rounded-md',
                  'border border-border/60 bg-background hover:bg-muted text-xs font-medium transition-colors',
                )}
              >
                <History className="h-3 w-3" />
                Histórico de consumo
              </button>
            </PopoverContent>
          </Popover>
        ) : (
          <UsageChip
            icon={<Coins className="h-3 w-3" />}
            pct={isFreeTier ? creditsConsumedPct : balance > 0 ? 0 : 100}
            primaryLabel={`${balance.toLocaleString('pt-BR')}`}
            ariaLabel={`Saldo de ${balance} créditos de conversão`}
            tooltipTitle="Créditos de Conversão"
            tooltipBody={
              <div className="space-y-0.5 text-muted-foreground">
                <div>
                  Saldo:{' '}
                  <span className={cn('font-medium', balance <= 0 ? 'text-destructive' : 'text-foreground')}>
                    {balance.toLocaleString('pt-BR')}
                  </span>
                </div>
                <div>Plano: <span className="text-foreground">{tier}</span></div>
                <div className="text-[11px] opacity-80">
                  Cada conversão consome 1 crédito em todos os planos.
                </div>
              </div>
            }
            forceTone={creditsTone}
          />
        )}

        {/* Atalho de upgrade (apenas admin) */}
        {isAdmin && !hideUpgradeButton && (
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => navigate('/tenant/plans?focus=referral')}
                className={cn(
                  'inline-flex items-center justify-center h-[22px] w-[22px] rounded-md',
                  'border border-border/40 text-muted-foreground hover:text-primary',
                  'hover:bg-primary/10 hover:border-primary/30 transition-colors',
                )}
                aria-label="Gerenciar plano de Indicação"
              >
                <ArrowUpRight className="h-3 w-3" />
              </button>
            </TooltipTrigger>
            <TooltipContent side="bottom" className="text-xs">
              Gerenciar plano de Indicação
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {/* Modal de Histórico de Consumo (renderizado apenas para admin) */}
      {isAdmin && (
        <ReferralCreditHistoryModal open={historyOpen} onOpenChange={setHistoryOpen} />
      )}
    </TooltipProvider>
  );
}
